import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { X, Save, Loader2 } from "lucide-react";

import { API_BASE_IMAGE_URL } from "../../../../config/apiConfig";
import { useProductViewModel } from "../../../../viewmodels/website/useProductViewModel";

import CategoryAsyncSelect from "./CategoryAsyncSelect";
import SubCategoryAsyncSelect from "./SubCategoryAsyncSelect";

interface EditProductModalProps {
  isOpen: boolean;
  productId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

interface ImageUploadCardProps {
  title: string;
  existingImage?: string;
  selectedFile: File | null;
  onFileChange: (file: File | null) => void;
}

const ImageUploadCard = ({
  title,
  existingImage,
  selectedFile,
  onFileChange,
}: ImageUploadCardProps) => {
  const imageUrl = selectedFile
    ? URL.createObjectURL(selectedFile)
    : existingImage
      ? `${API_BASE_IMAGE_URL}/${existingImage}`.replace(/([^:]\/)\/+/g, "$1")
      : "";

  return (
    <div>
      <label className="block text-sm font-semibold mb-2">{title}</label>

      <input
        type="file"
        accept="image/*"
        onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
        className="w-full border rounded-xl p-3"
      />

      {imageUrl ? (
        <img
          src={imageUrl}
          alt={title}
          className="mt-3 w-full h-40 object-contain border rounded-xl bg-slate-50"
          onError={(e) => {
            e.currentTarget.src = "/images/no-image.png";
          }}
        />
      ) : (
        <div className="mt-3 h-40 border rounded-xl flex items-center justify-center text-slate-400 bg-slate-50">
          No Image
        </div>
      )}
    </div>
  );
};

const EditProductModal = ({
  isOpen,
  productId,
  onClose,
  onSuccess,
}: EditProductModalProps) => {
  const { getProductById, updateProduct } = useProductViewModel();

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  //=========================================
  // FORM
  //=========================================

  const [form, setForm] = useState({
    name: "",
    description: "",

    categoryId: "",
    categoryName: "",

    subCategoryId: "",
    subCategoryName: "",

    modelNumber: "",

    capacity: "0",
    capacityText: "",

    division: "0",
    divisionText: "",

    unit: "",

    platformSize: "",

    material: "",
    displayType: "",
    powerSupply: "",
    communication: "",
    ingressProtection: "",
    operatingTemp: "",
    warranty: "",
  });

  //=========================================
  // NEW FILES
  //=========================================

  const [frontImage, setFrontImage] = useState<File | null>(null);
  const [backImage, setBackImage] = useState<File | null>(null);
  const [leftImage, setLeftImage] = useState<File | null>(null);
  const [rightImage, setRightImage] = useState<File | null>(null);
  const [attachment, setAttachment] = useState<File | null>(null);

  //=========================================
  // EXISTING FILES
  //=========================================

  const [existingFrontImage, setExistingFrontImage] = useState("");
  const [existingBackImage, setExistingBackImage] = useState("");
  const [existingLeftImage, setExistingLeftImage] = useState("");
  const [existingRightImage, setExistingRightImage] = useState("");
  const [existingAttachment, setExistingAttachment] = useState("");

  //=========================================
  // RESET
  //=========================================

  const resetForm = () => {
    setForm({
      name: "",
      description: "",

      categoryId: "",
      categoryName: "",

      subCategoryId: "",
      subCategoryName: "",

      modelNumber: "",

      capacity: "0",
      capacityText: "",

      division: "0",
      divisionText: "",

      unit: "",

      platformSize: "",

      material: "",
      displayType: "",
      powerSupply: "",
      communication: "",
      ingressProtection: "",
      operatingTemp: "",
      warranty: "",
    });

    setFrontImage(null);
    setBackImage(null);
    setLeftImage(null);
    setRightImage(null);
    setAttachment(null);

    setExistingFrontImage("");
    setExistingBackImage("");
    setExistingLeftImage("");
    setExistingRightImage("");
    setExistingAttachment("");
  };

  //=========================================
  // CLOSE
  //=========================================

  const closeModal = () => {
    resetForm();
    onClose();
  };

  //=========================================
  // LOAD PRODUCT
  //=========================================

  useEffect(() => {
    if (!isOpen || !productId) return;

    const load = async () => {
      setLoading(true);

      const product = await getProductById(productId);

      if (product) {
        setForm({
          name: product.name ?? "",
          description: product.description ?? "",

          categoryId: product.categoryId ?? "",
          categoryName: product.categoryName ?? "",

          subCategoryId: product.subCategoryId ?? "",
          subCategoryName: product.subCategoryName ?? "",

          modelNumber: product.modelNumber ?? "",

          capacity: String(product.capacity ?? "0"),
          capacityText: product.capacityText ?? "",

          division: String(product.division ?? "0"),
          divisionText: product.divisionText ?? "",

          unit: product.unit ?? "",

          platformSize: product.platformSize ?? "",

          material: product.material ?? "",
          displayType: product.displayType ?? "",
          powerSupply: product.powerSupply ?? "",
          communication: product.communication ?? "",
          ingressProtection: product.ingressProtection ?? "",
          operatingTemp: product.operatingTemp ?? "",
          warranty: product.warranty ?? "",
        });

        setExistingFrontImage(product.frontImageUrl ?? "");
        setExistingBackImage(product.backImageUrl ?? "");
        setExistingLeftImage(product.leftImageUrl ?? "");
        setExistingRightImage(product.rightImageUrl ?? "");
        setExistingAttachment(product.attachment ?? "");
      }

      setLoading(false);
    };

    load();
  }, [isOpen, productId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-y-auto">

        {/* ================= HEADER ================= */}

        <div className="sticky top-0 bg-white border-b px-6 py-5 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold">Edit Product</h2>

            <p className="text-sm text-gray-500 mt-1">
              Update product information.
            </p>
          </div>

          <button
            onClick={closeModal}
            className="p-2 rounded-xl hover:bg-slate-100"
          >
            <X size={22} />
          </button>
        </div>

        {/* ================= BODY ================= */}

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <Loader2 size={40} className="animate-spin text-primary" />
          </div>
        ) : (
          <div className="p-6">

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

              {/* Product Name */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Product Name *
                </label>

                <input
                  value={form.name}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      name: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              {/* Model */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Model Number
                </label>

                <input
                  value={form.modelNumber}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      modelNumber: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              {/* Category */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Category *
                </label>

                <CategoryAsyncSelect
                  value={
                    form.categoryId
                      ? {
                          value: form.categoryId,
                          label: form.categoryName,
                        }
                      : null
                  }
                  onChange={(selected) =>
                    setForm({
                      ...form,
                      categoryId: selected?.value ?? "",
                      categoryName: selected?.label ?? "",
                      subCategoryId: "",
                      subCategoryName: "",
                    })
                  }
                />
              </div>

              {/* Sub Category */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Sub Category
                </label>

                <SubCategoryAsyncSelect
                  categoryId={form.categoryId}
                  value={
                    form.subCategoryId
                      ? {
                          value: form.subCategoryId,
                          label: form.subCategoryName,
                        }
                      : null
                  }
                  onChange={(selected) =>
                    setForm({
                      ...form,
                      subCategoryId: selected?.value ?? "",
                      subCategoryName: selected?.label ?? "",
                    })
                  }
                />
              </div>

              {/* Capacity */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Capacity
                </label>

                <input
                  type="number"
                  min={0}
                  step="any"
                  value={form.capacity}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      capacity: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              {/* Capacity Text */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Capacity Text
                </label>

                <input
                  type="text"
                  value={form.capacityText}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      capacityText: e.target.value,
                    })
                  }
                  placeholder="e.g. 300 kg"
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              {/* Division */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Division
                </label>

                <input
                  type="number"
                  min={0}
                  step="any"
                  value={form.division}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      division: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              {/* Division Text */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Division Text
                </label>

                <input
                  type="text"
                  value={form.divisionText}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      divisionText: e.target.value,
                    })
                  }
                  placeholder="e.g. 100 g"
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              {/* Unit */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Unit
                </label>

                <select
                  value={form.unit}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      unit: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                >
                  <option value="">Select Unit</option>
                  <option value="kg">kg</option>
                  <option value="g">g</option>
                  <option value="mg">mg</option>
                  <option value="lb">lb</option>
                  <option value="oz">oz</option>
                  <option value="ton">ton</option>
                  <option value="t">t</option>
                </select>
              </div>

              {/* Platform Size */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Platform Size
                </label>

                <input
                  value={form.platformSize}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      platformSize: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              {/* Material */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Material
                </label>

                <input
                  value={form.material}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      material: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              {/* Display Type */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Display Type
                </label>

                <input
                  value={form.displayType}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      displayType: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              {/* Power Supply */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Power Supply
                </label>

                <input
                  value={form.powerSupply}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      powerSupply: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              {/* Communication */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Communication
                </label>

                <input
                  value={form.communication}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      communication: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              {/* Ingress Protection */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Ingress Protection
                </label>

                <input
                  value={form.ingressProtection}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      ingressProtection: e.target.value,
                    })
                  }
                  placeholder="IP65"
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              {/* Operating Temperature */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Operating Temperature
                </label>

                <input
                  value={form.operatingTemp}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      operatingTemp: e.target.value,
                    })
                  }
                  placeholder="-10°C ~ 40°C"
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              {/* Warranty */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Warranty
                </label>

                <input
                  value={form.warranty}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      warranty: e.target.value,
                    })
                  }
                  placeholder="12 Months"
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

            </div>

            {/* ================= DESCRIPTION ================= */}

            <div className="mt-6">
              <label className="block text-sm font-semibold mb-2">
                Description
              </label>

              <textarea
                rows={5}
                value={form.description}
                onChange={(e) =>
                  setForm({
                    ...form,
                    description: e.target.value,
                  })
                }
                placeholder="Product description..."
                className="w-full border rounded-xl px-4 py-3 resize-none"
              />
            </div>

            {/* ================= PRODUCT IMAGES ================= */}

            <div className="mt-8">
              <h3 className="text-lg font-semibold mb-4">Product Images</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">

                {/* FRONT */}

                <ImageUploadCard
                  title="Front Image"
                  existingImage={existingFrontImage}
                  selectedFile={frontImage}
                  onFileChange={(file) => setFrontImage(file)}
                />

                {/* BACK */}

                <ImageUploadCard
                  title="Back Image"
                  existingImage={existingBackImage}
                  selectedFile={backImage}
                  onFileChange={(file) => setBackImage(file)}
                />

                {/* LEFT */}

                <ImageUploadCard
                  title="Left Image"
                  existingImage={existingLeftImage}
                  selectedFile={leftImage}
                  onFileChange={(file) => setLeftImage(file)}
                />

                {/* RIGHT */}

                <ImageUploadCard
                  title="Right Image"
                  existingImage={existingRightImage}
                  selectedFile={rightImage}
                  onFileChange={(file) => setRightImage(file)}
                />

              </div>
            </div>

            {/* ================= ATTACHMENT ================= */}

            <div className="mt-8">
              <label className="block text-sm font-semibold mb-2">
                Attachment (PDF)
              </label>

              {existingAttachment && (
                <div className="mb-3">
                  <a
                    href={`${API_BASE_IMAGE_URL}/${existingAttachment}`.replace(
                      /([^:]\/)\/+/g,
                      "$1",
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    📄 Download Current Attachment
                  </a>
                </div>
              )}

              <input
                type="file"
                accept=".pdf"
                onChange={(e) =>
                  setAttachment(e.target.files?.[0] ?? null)
                }
                className="w-full border rounded-xl p-3"
              />

              {attachment && (
                <p className="mt-2 text-sm text-green-600">
                  New attachment: {attachment.name}
                </p>
              )}
            </div>

          </div>
        )}

        {/* ================= FOOTER ================= */}

        <div className="sticky bottom-0 bg-white border-t px-6 py-5 flex justify-end gap-3">

          <button
            type="button"
            onClick={closeModal}
            className="px-6 py-3 rounded-xl border hover:bg-slate-50"
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={saving}
            onClick={async () => {

              if (!form.name.trim()) {
                toast.error("Product name is required");
                return;
              }

              if (!form.categoryId) {
                toast.error("Category is required");
                return;
              }

              try {
                setSaving(true);

                const formData = new FormData();

                formData.append("id", productId);

                formData.append("name", form.name);
                formData.append("description", form.description);

                formData.append("categoryId", form.categoryId);
                formData.append("subCategoryId", form.subCategoryId);

                formData.append("modelNumber", form.modelNumber);

                // Existing numeric fields
                formData.append("capacity", form.capacity);
                formData.append("division", form.division);
                formData.append("unit", form.unit);

                // NEW TEXT FIELDS
                formData.append("capacityText", form.capacityText);
                formData.append("divisionText", form.divisionText);

                formData.append("platformSize", form.platformSize);
                formData.append("material", form.material);
                formData.append("displayType", form.displayType);
                formData.append("powerSupply", form.powerSupply);
                formData.append("communication", form.communication);
                formData.append("ingressProtection", form.ingressProtection);
                formData.append("operatingTemp", form.operatingTemp);
                formData.append("warranty", form.warranty);

                if (frontImage) {
                  formData.append("NewFrontImage", frontImage);
                }

                if (backImage) {
                  formData.append("NewBackImage", backImage);
                }

                if (leftImage) {
                  formData.append("NewLeftImage", leftImage);
                }

                if (rightImage) {
                  formData.append("NewRightImage", rightImage);
                }

                if (attachment) {
                  formData.append("NewAttachment", attachment);
                }

                const success = await updateProduct(formData);

                if (success) {
                  toast.success("Product updated successfully");

                  onSuccess?.();

                  closeModal();
                }

              } finally {
                setSaving(false);
              }
            }}
            className="bg-primary text-white px-6 py-3 rounded-xl flex items-center gap-2 disabled:opacity-60"
          >
            {saving ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Save size={18} />
                Update Product
              </>
            )}
          </button>

        </div>

      </div>
    </div>
  );
};

export default EditProductModal;
